<template>
    <header class="header">
        <div class="header__wrap">
            <div class="header__content">
                <div class="header__logo">
                    <img :src="headerLogo" v-if="headerLogo && !imgError" @error="imgError = true">
                    <SvgIcon :name="imgError ? 'empty' : defaultLogo" class="icon" v-else/>
                </div>
                <ul class="header__list">
                    <li class="header__item">
                        <button class="button button_view_outline button-outline-8" @click="openNewTab" :title="$t('text.Open Page in New Tab')">
                            <SvgIcon name="open-new-tab"/>
                        </button>
                        <span class="header-text">
                            {{ $t('text.Open Page in New Tab') }}
                        </span>
                    </li>
                    <li class="header__item" v-if="$consts.app.$isPortalSite && (hasSubusers || canReturn)">
                        <SubusersLoginMenu/>
                        <span class="header-text">
                            {{ $t('text.Subusers') }}
                        </span>
                    </li>
                    <li class="header__item">
                        <CheckboxControl
                            :control-class="''"
                            :label="$t('text.Dark mode')"
                            v-model="theme"
                            v-if="!$isAdminSite"
                        />
                    </li>
                    <li class="header__item">
                        <span>
                            <SvgIcon name="user" :title="getAppUser.login"/>
                        </span>
                        <span class="header-text">
                            {{getAppUser.login}}
                        </span>
                    </li>
                    <li class="header__item">
                        <button class="button button_view_outline" @click="onLogout">
                            {{ $t('menu.Logout') }}
                            <SvgIcon name="common__exit" class="icon-right"/>
                        </button>
                    </li>
                    <li class="header__item">
                        <button class="button button_icon" @click="displaySectionWidget('SectionWidgetPMUserEdit')">
                            <SvgIcon name="wizard-settings" class="icon" :title="$t('menu.Settings')"/>
                        </button>
                    </li>
                    <template v-if="$consts.app.$isPrometheus && $consts.app.$isPortalSite">
                        <li class="header__item header__item-line"></li>
                        <li class="header__item" @click="$router.push({name: $consts.routerNames.notificationsevents.main})" :title="$t('menu.Notifications')">
                            <span class="header__item-badge">
                                <SvgIcon name="icon-dialog" class="icon"/>
                                <span class="header__item-badge__info" v-show="notifications">{{ notifications }}</span>
                            </span>
                            <span class="header-text">
                                {{ $t('menu.Notifications') }}
                            </span>
                        </li>
                        <li class="header__item header__item-line"></li>
                        <li class="header__item" @click="$router.push({name: $consts.routerNames.md.fleet.widget, params: {widget: 'camera_errors'}})">
                            <span class="header__item-badge" :title="$t('errors.Camera Errors')">
                                <SvgIcon name="warning" class="icon"/>
                                <span class="header__item-badge__info header__item-badge__info--danger" v-show="videoStatusesErrorsUnit">{{ videoStatusesErrorsUnit }}</span>
                            </span>
                            <span class="header-text">
                            {{ $t('errors.Camera Errors') }}
                            </span>
                        </li>
                    </template>
                </ul>
            </div>
        </div>
    </header>
</template>

<script>
import {mapGetters, mapActions, mapMutations} from "vuex";
import SubusersLoginMenu from "@/components/app/SubusersLoginMenu.vue";
import {userStatus} from "@/lib/user-status";
import {canReturnToParent} from "@/lib/auth-storage";

export default {
    name: "SectionHeader",
    mixins: [],
    components: {SubusersLoginMenu},
    data() {
        return {
            imgError: false,
            canReturn: canReturnToParent(),
        }
    },
    computed:{
        ...mapGetters([
            "getAppUser",
            "getNewEventsCount",
            "getNewNotificationEventsCount",
            "getTimeToday",
            "getLastVisitNotifications",
            "getTheme",
            "getVideoStatusesErrors",
            "getUnitsByIds",
            "getUsers",
        ]),
        hasSubusers() {
            return this.getUsers.some(u => u.id != this.getAppUser.id && u.status != userStatus.DELETED)
        },
        headerLogo() {
            return this.getAppUser?.head__logo?.url
        },
        defaultLogo() {
            return this.$consts.app.$isPrometheus ? 'pm-logo' : 'intuit-logo'
        },
        unitsIds() {
            return Object.keys(this.getUnitsByIds);
        },
        videoStatusesErrors() {
            return this.getVideoStatusesErrors.filter(vs => this.unitsIds.includes(vs.unit_id))
        },
        videoStatusesErrorsUnit() {
            return this.videoStatusesErrors?.length
        },
        theme: {
            get() {
                return this.getTheme == this.$consts.themes.dark
            },
            set(vl) {
                this.setTheme(vl ? this.$consts.themes.dark : this.$consts.themes.white)
            }
        },
        notifications(){
            let count = this.getNewEventsCount + this.getNewNotificationEventsCount
            let plus = (this.getTimeToday > this.getLastVisitNotifications ? '+' : '')
            return (count || plus) ? count + plus : false
        },
    },
    methods:{
        ...mapMutations([
            "setSectionWidget",
            "setTheme",
        ]),
        ...mapActions([
            "logout",
        ]),
        openNewTab() {
            window.open(this.$route.fullPath, '_blank')
        },
        onLogout(){
            //this.$cookies.set('authtoken', '')
            this.logout()
        },
        displaySectionWidget(component) {
            this.setSectionWidget({
                component: component,
                props: {}
            })
        },
    }
}
</script>
<style lang="scss" scoped>
.button-outline-8 {
    border-radius: 8px;
    padding: 8px 5px 7px 8px;
}
</style>
<style lang="scss">
.header {
    grid-area: header;
    background: var(--header-background);
    box-shadow: var(--header-box-shadow);
    position: relative;
    z-index: 6;
    &__wrap {
        padding: 5px 25px;
        height: 100%;
    }
    &__content{
        display: flex;
        justify-content: space-between;
        align-items: center;
        height: 100%;
    }
    &__logo {
        height: 100%;
        img{
            height: 100%;
        }
    }
    &__list {
        display: flex;
        align-items: center;
        gap: 15px;
        height: 100%;
    }
    &__item{
        display: flex;
        align-items: center;
        gap: 10px;
        color: white;
        cursor: pointer;
        @include font(14px, 20px, 400, none, normal, var(--header-color));

        &:hover {
            text-decoration: underline;
        }
    }
    &__item-line {
        height: 70%;
        width: 1px;
        background: var(--header-line-item);
    }
    &__item-badge {
        display: flex;
        justify-content: center;
        align-items: center;
        border-radius: 8px;
        border: solid 1px var(--header-border-color-notifications);
        padding: 5px;
        width: 32px;
        height: 32px;
        position: relative;
        cursor: pointer;

        &__info {
            position: absolute;
            top: -2px;
            right: 0;
            transform: translateX(50%);
            background: var(--header-notifications-info);
            padding: 1px 4px;
            display: flex;
            justify-content: center;
            align-items: center;
            @include font(8px, normal, 400, none, normal, var(--color-white));
            border-radius: 100%;

            &--danger {
                background: var(--color-danger);
            }
        }
    }
    .checkbox-control {
        &__text {
            @include font(14px, 20px, 400, none, normal, var(--header-color));
            @include adaptive-font(14, 12)
        }
    }
}
.prometheus-portal {
    .header {
        height: 50px;
        &__item {
            @include adaptive-font(12, 10);
            &:last-child {
                width: 180px;
            }
        }
        &__logo {
            margin-left: 67px;
            height: 100%;
            display: flex;
            justify-content: flex-start;
            align-items: center;

            .svg-icon {
                height: 100%;
                width: 180px;
            }
        }
        .checkbox-control {
            &__text {
                @include font(12px, normal, 400, none, normal, var(--header-color));
                @include adaptive-font(12, 10)
            }
            &__switch {
                height: 1rem;
                &:before {
                    top: 1px;
                    height: 14px;
                    width: 14px;
                }
            }
        }
    }
}
</style>